import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { AuthService } from "@sicapes/auth";
import { ConfirmationService } from "primeng/api";
import { RecursoService } from "src/app/shared/service/inscricao/recurso.service";
import { Router } from "@angular/router";

/**
 * Situação única de uma etapa. A prioridade de resolução é
 * ATIVO > ENVIADO > EXPIRADO, para que uma mesma etapa nunca apareça
 * em duas seções ao mesmo tempo (bug da versão anterior).
 */
export type SituacaoEtapa = 'ATIVO' | 'ENVIADO' | 'EXPIRADO';
export type FiltroSituacao = 'TODAS' | SituacaoEtapa;

export interface DocumentoReconsideracao {
    id?: number;
    tpArquivo: 'J' | 'P' | 'A';
    nmArquivo: string;
    _links?: { self?: { href?: string } };
}

export interface EtapaOcorrenciaView {
    /** Registro original (uma Reconsideracao = ocorrência × etapa). */
    reconsideracao: any;
    idEtapaAnalise: number;
    nomeEtapa: string;
    situacao: SituacaoEtapa;
    documentosCarregados: boolean;
    justificativa: DocumentoReconsideracao | null;
    anexos: DocumentoReconsideracao[];
    anexosAbertos: boolean;
}

/** Parecer emitido pelo SAP (GET /rest/analise/parecer?idOcorrencia). */
export interface ParecerAnalise {
    id: number;
    nomeEtapa?: string;
    nmArquivo: string;
    dsUrlArquivo: string;
    /** ISO 8601 sem fuso, ex.: 2026-07-23T14:38:18 */
    dataHoraAnexacao?: string;
}

export type CargaPareceres = 'PENDENTE' | 'CARREGANDO' | 'CARREGADO' | 'ERRO';

export interface OcorrenciaView {
    idOcorrencia: number;
    tipoRecursoEsperado: 'E' | 'P' | null;
    etapas: EtapaOcorrenciaView[];
    totalPorSituacao: Record<SituacaoEtapa, number>;
    pareceres: ParecerAnalise[];
    pareceresAbertos: boolean;
    cargaPareceres: CargaPareceres;
}

@Component({
    selector: 'app-dashboard-ocorrencia',
    templateUrl: './dashboard-ocorrencia.component.html',
    styleUrls: ['./dashboard-ocorrencia.component.less'],
    providers: [ConfirmationService]
})
export class DashboardOcorrencia implements OnInit, OnChanges {
    @Input() ocorrencias: any[] = [];

    /** Lista agrupada por ocorrência, montada em ngOnChanges (nunca no template). */
    ocorrenciasAgrupadas: OcorrenciaView[] = [];
    filtro: FiltroSituacao = 'TODAS';

    /** Quantidade de ocorrências que possuem ao menos uma etapa em cada situação. */
    contadores: Record<FiltroSituacao, number> = { TODAS: 0, ATIVO: 0, ENVIADO: 0, EXPIRADO: 0 };
    totalEtapas = 0;

    readonly filtrosDisponiveis: { valor: FiltroSituacao; rotulo: string }[] = [
        { valor: 'TODAS', rotulo: 'Todas' },
        { valor: 'ATIVO', rotulo: 'Em recurso' },
        { valor: 'ENVIADO', rotulo: 'Encaminhadas' },
        { valor: 'EXPIRADO', rotulo: 'Expiradas' },
    ];

    /** Id do parecer cujo download está em andamento. */
    parecerBaixando: number | null = null;

    constructor(
        protected recursoService: RecursoService,
        protected authService: AuthService,
        protected router: Router
    ) { }

    ngOnInit(): void {
        // TODO: O QUE GERE O SIMULADOR DE ACESSO NÃO PODE FICAR EM CADA COMPONENTE, DEVE SER ALGUM INTERCEPTOR QUE DEVE GERENCIAR
        this.authService.currentUser.subscribe((x: any) => {
            if (x && ('corporativo' in x) &&
                ('pessoaLogada' in x) &&
                x.corporativo.codigo !== x.pessoaLogada.codigo) {
                this.recursoService.simuladorAcesso = btoa(x.corporativo.codigo);
            } else {
                this.recursoService.simuladorAcesso = null;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.ocorrencias) {
            this.ocorrenciasAgrupadas = this.agruparPorOcorrencia(this.ocorrencias || []);
            this.atualizarContadores();
            this.carregarDocumentosEnviados();
        }
    }

    // ---------------------------------------------------------------
    // Montagem do view-model
    // ---------------------------------------------------------------

    public situacaoDaEtapa(reconsideracao: any): SituacaoEtapa {
        const informacoes: any[] = reconsideracao?.reconsideracaoInformacao || [];
        const possuiSubmetido = informacoes.some((ri) => ri.submetido);
        const possuiPendente = informacoes.some((ri) => !ri.submetido);

        if (!reconsideracao?.expirado && possuiPendente) { return 'ATIVO'; }
        if (possuiSubmetido) { return 'ENVIADO'; }
        return 'EXPIRADO';
    }

    public agruparPorOcorrencia(lista: any[]): OcorrenciaView[] {
        const mapa = new Map<number, OcorrenciaView>();

        lista.forEach((reconsideracao) => {
            const idOcorrencia = reconsideracao?.reconsideracaoOcorrencia?.idOcorrencia;
            if (idOcorrencia === undefined || idOcorrencia === null) { return; }

            if (!mapa.has(idOcorrencia)) {
                mapa.set(idOcorrencia, {
                    idOcorrencia,
                    tipoRecursoEsperado: reconsideracao.tipoRecursoEsperado || null,
                    etapas: [],
                    totalPorSituacao: { ATIVO: 0, ENVIADO: 0, EXPIRADO: 0 },
                    pareceres: [],
                    pareceresAbertos: false,
                    cargaPareceres: 'PENDENTE',
                });
            }

            const ocorrencia = mapa.get(idOcorrencia);
            const situacao = this.situacaoDaEtapa(reconsideracao);

            ocorrencia.etapas.push({
                reconsideracao,
                idEtapaAnalise: reconsideracao.reconsideracaoOcorrencia.idEtapaAnalise,
                nomeEtapa: reconsideracao.situacaoAnalise,
                situacao,
                documentosCarregados: false,
                justificativa: null,
                anexos: [],
                anexosAbertos: false,
            });
            ocorrencia.totalPorSituacao[situacao]++;
        });

        // Ocorrências com etapa "Em recurso" (ATIVO) ficam no topo da lista.
        return Array.from(mapa.values()).sort((a, b) => {
            const aAtivo = a.totalPorSituacao.ATIVO > 0 ? 0 : 1;
            const bAtivo = b.totalPorSituacao.ATIVO > 0 ? 0 : 1;
            return aAtivo - bAtivo;
        });
    }

    protected atualizarContadores(): void {
        this.contadores = { TODAS: this.ocorrenciasAgrupadas.length, ATIVO: 0, ENVIADO: 0, EXPIRADO: 0 };
        this.totalEtapas = 0;

        this.ocorrenciasAgrupadas.forEach((o) => {
            this.totalEtapas += o.etapas.length;
            (['ATIVO', 'ENVIADO', 'EXPIRADO'] as SituacaoEtapa[]).forEach((s) => {
                if (o.totalPorSituacao[s] > 0) { this.contadores[s]++; }
            });
        });
    }

    // ---------------------------------------------------------------
    // Filtro
    // ---------------------------------------------------------------

    public selecionarFiltro(filtro: FiltroSituacao): void {
        this.filtro = filtro;
    }

    /** Ocorrências visíveis para o filtro atual (só as que têm etapa naquela situação). */
    get ocorrenciasVisiveis(): OcorrenciaView[] {
        if (this.filtro === 'TODAS') { return this.ocorrenciasAgrupadas; }
        return this.ocorrenciasAgrupadas.filter((o) => o.totalPorSituacao[this.filtro as SituacaoEtapa] > 0);
    }

    public etapasVisiveis(ocorrencia: OcorrenciaView): EtapaOcorrenciaView[] {
        if (this.filtro === 'TODAS') { return ocorrencia.etapas; }
        return ocorrencia.etapas.filter((e) => e.situacao === this.filtro);
    }

    /** Situação "dominante" da ocorrência, usada no chip do cabeçalho do card. */
    public situacaoResumo(ocorrencia: OcorrenciaView): SituacaoEtapa {
        if (ocorrencia.totalPorSituacao.ATIVO > 0) { return 'ATIVO'; }
        if (ocorrencia.totalPorSituacao.ENVIADO > 0) { return 'ENVIADO'; }
        return 'EXPIRADO';
    }

    // ---------------------------------------------------------------
    // Documentos enviados (justificativa + anexos)
    // ---------------------------------------------------------------

    protected carregarDocumentosEnviados(): void {
        this.ocorrenciasAgrupadas.forEach((ocorrencia) => {
            ocorrencia.etapas
                .filter((etapa) => etapa.situacao === 'ENVIADO' && !etapa.documentosCarregados)
                .forEach((etapa) => this.carregarDocumentos(ocorrencia, etapa));
        });
    }

    public carregarDocumentos(ocorrencia: OcorrenciaView, etapa: EtapaOcorrenciaView): void {
        etapa.documentosCarregados = true;
        this.recursoService.obterDocumentosReconsideraco(ocorrencia.idOcorrencia, etapa.idEtapaAnalise).subscribe(
            (response: any) => {
                const documentos: DocumentoReconsideracao[] = response?._embedded?.items || [];
                etapa.justificativa = documentos.find((d) => d.tpArquivo === 'J') || null;
                etapa.anexos = documentos.filter((d) => d.tpArquivo === 'A');
            },
            () => {
                etapa.justificativa = null;
                etapa.anexos = [];
            }
        );
    }

    public alternarAnexos(etapa: EtapaOcorrenciaView): void {
        etapa.anexosAbertos = !etapa.anexosAbertos;
    }

    // ---------------------------------------------------------------
    // Ações
    // ---------------------------------------------------------------

    public preencherRecurso(reconsideracao: any): void {
        this.recursoService.setReconsideracaoAtiva(reconsideracao);
    }

    // ---------------------------------------------------------------
    // Pareceres da análise (linha do tempo, carregada ao expandir)
    // ---------------------------------------------------------------

    public alternarPareceres(ocorrencia: OcorrenciaView): void {
        ocorrencia.pareceresAbertos = !ocorrencia.pareceresAbertos;
        const precisaCarregar = ocorrencia.cargaPareceres === 'PENDENTE' || ocorrencia.cargaPareceres === 'ERRO';
        if (ocorrencia.pareceresAbertos && precisaCarregar) {
            this.carregarPareceres(ocorrencia);
        }
    }

    public carregarPareceres(ocorrencia: OcorrenciaView): void {
        ocorrencia.cargaPareceres = 'CARREGANDO';
        this.recursoService.obterDocumentosParecer(ocorrencia.idOcorrencia).subscribe(
            (response: any) => {
                ocorrencia.pareceres = this.ordenarPorAnexacao(response?._embedded?.items || []);
                ocorrencia.cargaPareceres = 'CARREGADO';
            },
            () => {
                ocorrencia.pareceres = [];
                ocorrencia.cargaPareceres = 'ERRO';
            }
        );
    }

    /** Ordem cronológica de anexação; pareceres sem data vão para o fim, na ordem do SAP. */
    protected ordenarPorAnexacao(pareceres: ParecerAnalise[]): ParecerAnalise[] {
        return pareceres
            .map((parecer, indice) => ({ parecer, indice }))
            .sort((a, b) => {
                const dataA = a.parecer.dataHoraAnexacao || '';
                const dataB = b.parecer.dataHoraAnexacao || '';
                if (dataA && dataB && dataA !== dataB) { return dataA < dataB ? -1 : 1; }
                if (!dataA !== !dataB) { return dataA ? -1 : 1; }
                return a.indice - b.indice;
            })
            .map((item) => item.parecer);
    }

    public downloadDocumentoParecer(parecer: ParecerAnalise): void {
        if (this.parecerBaixando !== null) { return; }
        this.parecerBaixando = parecer.id;
        this.recursoService.downloadDocumentosParecerAnalise(parecer).subscribe(
            (response: any) => {
                this.downloadPDF(response.arquivo, parecer.nmArquivo);
                this.parecerBaixando = null;
            },
            () => {
                this.parecerBaixando = null;
            }
        );
    }

    downloadPDF(conteudo: string, nomeArquivo: string = 'parecer.pdf'): void {
        const downloadLink = document.createElement("a");
        downloadLink.href = conteudo;
        downloadLink.download = nomeArquivo;
        downloadLink.click();
    }
}
